import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import type {
  Event,
  Experiment,
  GeneralEvent,
  GetVariationMapResult,
  OptimizelyGetAPI,
  OptimizelyPushAPI,
  Variation,
} from "./types";

type ContextValue = {
  activeExperiments?: string[];
  events?: Record<string, Event>;
  experiments?: Record<string, Experiment>;
  fireEvent?: (event: GeneralEvent) => void;
  variationMap?: GetVariationMapResult;
  variations?: Record<string, Variation>;
};

const OptimizelyContext = createContext<ContextValue>({});

type Props = {
  children: ReactNode;
};

export const OptimizelyContextProvider: React.FunctionComponent<Props> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  const [activeExperiments, setActiveExperiments] = useState<string[]>([]);
  const [events, setEvents] = useState<Record<string, Event>>({});
  const [experiments, setExperiments] = useState<Record<string, Experiment>>({});
  const [variationMap, setVariationMap] = useState<GetVariationMapResult>({});
  const [variations, setVariations] = useState<Record<string, Variation>>({});

  useEffect(() => {
    const run = () => {
      if (window.optimizely) {
        setInitialized(true);
        return;
      }
      requestAnimationFrame(run);
    };
    requestAnimationFrame(run);
  }, []);

  useEffect(() => {
    if (!initialized) return;
    setActiveExperiments(window.optimizely.get("state").getActiveExperimentIds());
    setEvents(window.optimizely.get("data").events);
    setExperiments(window.optimizely.get("data").experiments);
    setVariationMap(window.optimizely.get("state").getVariationMap());
    setVariations(window.optimizely.get("data").variations);
  }, [initialized]);

  const fireEvent = useCallback(
    (event: GeneralEvent) => {
      if (Object.values(events).every(event_ => event_.name !== event.eventName)) {
        console.warn(`Event "${event.eventName}" is not defined in Optimizely.`);
        return;
      }
      window.optimizely.push(event);
    },
    [events],
  );

  return (
    <OptimizelyContext.Provider
      value={{
        activeExperiments,
        events,
        experiments,
        fireEvent,
        variationMap,
        variations,
      }}
    >
      {children}
    </OptimizelyContext.Provider>
  );
};

export const useOptimizelyContext = () => useContext(OptimizelyContext);

declare global {
  interface Window {
    optimizely: {
      get: OptimizelyGetAPI;
      push: OptimizelyPushAPI;
    };
  }
}
